import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getAccessibleModules } from "@/config/modules";
import client from "@/api/client";

export default function DashboardPage() {
  const { user } = useAuth();
  const modules = getAccessibleModules(user?.role).filter((m) => m.key !== "dashboard");
  const [counts, setCounts] = useState({});

  useEffect(() => {
    const allowed = getAccessibleModules(user?.role).map((m) => m.key);
    const load = async () => {
      const next = {};
      if (allowed.includes("employee-master")) {
        try {
          next.employees = (await client.get("/employees", { params: { all: true } })).data.length;
        } catch {
          /* ignore */
        }
      }
      if (allowed.includes("asset-register")) {
        try {
          next.assets = (await client.get("/assets")).data.length;
        } catch {
          /* ignore */
        }
      }
      if (allowed.includes("site-master")) {
        try {
          next.sites = (await client.get("/site-master/sites", { params: { all: true } })).data.length;
        } catch {
          /* ignore */
        }
      }
      setCounts(next);
    };
    load();
  }, [user?.role]);

  const stats = [
    { label: "Active Employees", value: counts.employees, key: "employees" },
    { label: "Registered Assets", value: counts.assets, key: "assets" },
    { label: "Active Sites", value: counts.sites, key: "sites" },
  ].filter((s) => s.value !== undefined);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <p className="text-sm text-slate-500 font-mono uppercase tracking-widest">Welcome back</p>
        <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="dashboard-user-name">
          {user?.name}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {user?.role} · {user?.employee_id}
        </p>
      </div>

      {stats.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="dashboard-stats">
          {stats.map((s) => (
            <div key={s.key} className="rounded-lg border border-slate-200 bg-white p-5" data-testid={`dashboard-stat-${s.key}`}>
              <p className="text-xs font-mono uppercase tracking-widest text-slate-400">{s.label}</p>
              <p className="text-3xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {s.value}
              </p>
            </div>
          ))}
        </div>
      )}

      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
          Your Modules
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-modules-grid">
          {modules.map((m) => {
            const Icon = m.icon;
            return (
              <Link
                key={m.key}
                to={m.path}
                data-testid={`dashboard-module-tile-${m.key}`}
                className="group rounded-lg border border-slate-200 bg-white p-5 hover:border-amber-500 hover:shadow-md transition-all"
              >
                <div className="h-10 w-10 rounded-md bg-slate-900 group-hover:bg-amber-600 flex items-center justify-center transition-colors">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <p className="mt-3 font-semibold text-slate-900">{m.title}</p>
                <p className="text-xs text-slate-500 mt-1">{m.description}</p>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
